/* eslint-disable */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, PlugTypeIcon } from '@ui';
import {
  formatChargingCountdown,
  getPortInRepairMessage,
  isDcPort,
  isStationInRepair,
  plugReportsConnectedStatus,
  plugTypeMaping,
} from '../../utils/utils';
import { resolvePortPriceStickerBand, SOC_TIER_BAND_FILL } from '../../utils/socDynamicPriceNotes';
import { usePortStatus } from '../../hooks/usePortStatus';
import './PortSummaryCard.css';

const PORT_SUMMARY_PLUG_LABELS = {
  TYPE_1: 'Type 1',
  TYPE_2: 'Type 2',
  TYPE_2_NO_CABBLE: 'Type 2',
  GBT: 'GB/T',
  EURO_SOCKET: 'Euro',
  CCS1: 'CCS Combo 1',
  CCS2: 'CCS Combo 2',
  CCS_2: 'CCS Combo 2',
  GBT_DC: 'GB/T DC',
  CHADEMO: 'CHAdeMO',
  NACS: 'NACS',
};

export function portSummaryPlugLabel(plugType, t) {
  if (plugType === 'TYPE_2_NO_CABBLE') {
    const suffix = typeof t === 'function' ? t('plugTypeNoCable') : 'без кабелю';
    return `Type 2 ${suffix}`;
  }
  return PORT_SUMMARY_PLUG_LABELS[plugType] || plugTypeMaping[plugType] || '';
}

export default function PortSummaryCard({
  station,
  portNumber,
  portCount = 1,
  socPercent = null,
  showCountdown = true,
  /** When false, skip live polling (list page uses station.job / closestPortsDeviceStatus). */
  livePoll = true,
  /** Static status used when livePoll is false. */
  portStatusOverride = null,
  onClick,
  ariaLabel,
  className = '',
  style,
}) {
  const { t } = useTranslation();
  const number = portNumber ?? station?.portNumber ?? station?.number;
  const { portStatus: livePortStatus } = usePortStatus(livePoll ? number : null);
  const portStatus = portStatusOverride || livePortStatus;
  const [countdownSeconds, setCountdownSeconds] = useState(null);
  const isInService = station?.name?.toLowerCase().includes('inservice');
  const isInRepair = isStationInRepair(station);
  const repairMessage = isInRepair ? getPortInRepairMessage(station, t) : null;
  const plugLabel = portSummaryPlugLabel(station?.plugType, t);
  const reportsConnected = plugReportsConnectedStatus(station?.plugType);
  const isRoamingBusy = Boolean(portStatus.isOccupied) && !portStatus.isInProgress;
  const isChargingView = portStatus.isInProgress || isRoamingBusy;
  const showConnected =
    !isInRepair && station?.online !== false && reportsConnected && portStatus.isReady && !isChargingView;
  const isClickable = Boolean(onClick) && !isInRepair;

  useEffect(() => {
    if (!portStatus.isInProgress || portStatus.leftSeconds == null) {
      setCountdownSeconds(null);
      return undefined;
    }
    setCountdownSeconds(portStatus.leftSeconds);
    const tick = window.setInterval(() => {
      setCountdownSeconds(prev => (prev == null ? null : Math.max(0, prev - 1)));
    }, 1000);
    return () => window.clearInterval(tick);
  }, [portStatus.isInProgress, portStatus.leftSeconds]);

  const chargingCountdown =
    showCountdown && portStatus.isInProgress && countdownSeconds != null
      ? formatChargingCountdown(countdownSeconds)
      : null;

  const cardStateClass = useMemo(() => {
    if (isInRepair) return ' start-many-port-card--repair';
    if (isChargingView && station?.online === false) {
      return ' start-many-port-card--charging start-many-port-card--session-offline';
    }
    if (isChargingView) return ' start-many-port-card--charging';
    if (isInService) return ' start-many-port-card--inservice';
    if (station?.online === false) return ' start-many-port-card--offline';
    return ' start-many-port-card--available';
  }, [isInRepair, isInService, station?.online, isChargingView]);

  const priceUah = useMemo(() => {
    if (station?.costPerKwt == null || station?.costPerKwt === undefined) {
      return null;
    }
    return Number((station.costPerKwt / 100).toFixed(2));
  }, [station?.costPerKwt]);

  const priceBand = useMemo(
    () =>
      priceUah == null ? null : resolvePortPriceStickerBand({ station, socPercent, costPerKwt: station.costPerKwt }),
    [priceUah, station, socPercent]
  );

  const powerDisplay = useMemo(() => {
    if (portStatus.isInProgress) {
      const liveKw = Number(portStatus.powerKw);
      if (Number.isFinite(liveKw) && liveKw > 0) {
        const formatted = liveKw >= 10 ? liveKw.toFixed(0) : liveKw.toFixed(1);
        return { value: formatted, unit: 'kW' };
      }
      return { value: '0', unit: 'kW' };
    }
    const maxKw = station?.maxPowerWt ? station.maxPowerWt / 1000 : null;
    if (!Number.isFinite(maxKw) || maxKw <= 0) {
      return null;
    }
    const maxRounded = Math.round(maxKw);
    const count = Math.max(1, Number(portCount) || 1);
    const showSharedRange = isDcPort(station?.plugType, station?.maxPowerWt) && count > 1;
    if (showSharedRange) {
      const minRounded = Math.round(maxKw / count);
      if (minRounded !== maxRounded) {
        return { value: `${minRounded} – ${maxRounded}`, unit: 'kW', isRange: true };
      }
    }
    return { value: String(maxRounded), unit: 'kW' };
  }, [portStatus.isInProgress, portStatus.powerKw, station?.maxPowerWt, station?.plugType, portCount]);

  const powerCaption = isInRepair ? repairMessage : isChargingView ? t('inProgress') : t('startManyMaxPower');

  const handleKeyDown = e => {
    if (!isClickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const statusTitle = isInRepair
    ? repairMessage
    : isChargingView
      ? t('inProgress')
      : showConnected
        ? t('connected')
        : t('listPortAvailable');

  return (
    <Card
      variant="invert"
      className={`start-many-port-card charging-port-card${isClickable ? ' charging-port-card--clickable' : ''}${cardStateClass}${showConnected ? ' start-many-port-card--connected' : ''} ${className}`.trim()}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      aria-disabled={isInRepair || undefined}
      aria-label={isInRepair && repairMessage ? `${ariaLabel || ''} ${repairMessage}`.trim() : ariaLabel}
      style={style}
    >
      <div className="start-many-port-card__index">
        <span className="start-many-port-card__index-prefix">{t('station').trim()}</span>
        <span className="start-many-port-card__index-number">{number}</span>
      </div>
      <span className="start-many-port-card__status-dot" aria-hidden="true" title={statusTitle} />
      <div className="start-many-port-card__content">
        <div className="start-many-port-card__body">
          <div className="start-many-port-card__specs">
            <span className="start-many-port-card__power-label">{powerCaption}</span>
            {powerDisplay ? (
              <p
                className={`start-many-port-card__power${
                  powerDisplay.isRange ? ' start-many-port-card__power--range' : ''
                }`}
              >
                <span className="start-many-port-card__power-value">{powerDisplay.value}</span>
                <span className="start-many-port-card__power-unit"> {powerDisplay.unit}</span>
              </p>
            ) : null}
            {priceUah != null ? (
              <p
                className={`start-many-port-card__price ${
                  priceBand ? `start-many-port-card__price--${priceBand}` : 'start-many-port-card__price--plain'
                }`}
                style={priceBand ? { background: SOC_TIER_BAND_FILL[priceBand] } : undefined}
              >
                <span className="start-many-port-card__price-value">{priceUah}</span>
                <span className="start-many-port-card__price-unit"> {t('portPriceStickerUnit')}</span>
              </p>
            ) : null}
          </div>
          <div className="start-many-port-card__connector">
            {plugLabel ? (
              <span className="start-many-port-card__connector-name">
                {station?.plugType === 'TYPE_2_NO_CABBLE' ? (
                  <>
                    Type 2 <span className="start-many-port-card__connector-nocable">{t('plugTypeNoCable')}</span>
                  </>
                ) : (
                  plugLabel
                )}
              </span>
            ) : null}
            <div className="start-many-port-card__connector-logo">
              <PlugTypeIcon plugType={station?.plugType} size="4rem" className="start-many-port-card__connector-icon" />
            </div>
            {chargingCountdown ? (
              <p className="start-many-port-card__countdown" aria-label={`~${chargingCountdown}`}>
                ~{chargingCountdown}
              </p>
            ) : null}
            {showConnected ? <span className="start-many-port-card__connector-status">{t('connected')}</span> : null}
          </div>
        </div>
        {isInService && (
          <div className="b2b-inservice-warning start-many-port-card__inservice" role="alert">
            ⚠️ {t('inServiceWarning')}
          </div>
        )}
      </div>
    </Card>
  );
}
